/**
 * The realtime side of vehicles: `vehicle.pose` goes out at most ten times a second while the user
 * drives, and arrives for every other driver in view. The realtime client attaches its send function
 * after `auth.ok` and routes inbound messages here; in mock mode (no socket) a loopback hook lets
 * tests play another driver.
 */

export interface PoseMessage {
  type: "vehicle.pose";
  vehicleId: string;
  x: number;
  z: number;
  yawDeg: number;
  speed: number;
}

type Send = (message: PoseMessage) => void;

class VehicleHub {
  private send: Send | null = null;
  private listeners = new Set<(m: PoseMessage) => void>();
  /** Poses sent (mock mode), newest last, for tests. */
  readonly sent: PoseMessage[] = [];

  attach(send: Send): void {
    this.send = send;
  }

  detach(): void {
    this.send = null;
  }

  /** Routes a server message; returns true when it was a vehicle message. */
  ingest(message: unknown): boolean {
    const m = message as Partial<PoseMessage> | null;
    if (!m || m.type !== "vehicle.pose") return false;
    if (typeof m.vehicleId !== "string") return true;
    const pose: PoseMessage = {
      type: "vehicle.pose",
      vehicleId: m.vehicleId,
      x: Number(m.x) || 0,
      z: Number(m.z) || 0,
      yawDeg: Number(m.yawDeg) || 0,
      speed: Number(m.speed) || 0,
    };
    for (const l of this.listeners) l(pose);
    return true;
  }

  onPose(l: (m: PoseMessage) => void): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  sendPose(m: Omit<PoseMessage, "type">): void {
    const message: PoseMessage = { type: "vehicle.pose", ...m };
    if (import.meta.env["VITE_UZ_MOCK"] === "1") {
      this.sent.push(message);
      if (this.sent.length > 200) this.sent.shift();
    }
    this.send?.(message);
  }
}

export const vehicleHub = new VehicleHub();
